import type { Color } from '../../../types/theme';
import { rgba } from '../../../utils/colors';
import { backgroundComplementary } from './background-complementary';
import { borderComplementary } from './border-complementary';
import { graphics } from './graphics';

const color: Color = {
  background: {
    accent: {
      active: opacity => rgba(108, 41, 255, opacity),
      contrast: opacity => rgba(67, 0, 214, opacity),
      default: opacity => rgba(108, 41, 255, opacity),
      disabled: opacity => rgba(199, 182, 237, opacity),
      hover: opacity => rgba(67, 0, 214, opacity),
    },
    complementary: (color, opacity) => backgroundComplementary[color](opacity),
    danger: {
      active: opacity => rgba(216, 63, 85, opacity),
      default: opacity => rgba(216, 63, 85, opacity),
      disabled: opacity => rgba(215, 188, 192, opacity),
      hover: opacity => rgba(165, 34, 53, opacity),
    },
    error: {
      default: opacity => rgba(250, 230, 230, opacity),
    },
    graphics: (color, opacity) => graphics[color](opacity),
    info: {
      default: opacity => rgba(223, 247, 253, opacity),
    },
    premium: {
      active: opacity => rgba(239, 196, 75, opacity),
      default: opacity => rgba(239, 196, 75, opacity),
      disabled: opacity => rgba(199, 195, 211, opacity),
      hover: opacity => rgba(235, 163, 19, opacity),
    },
    primary: {
      active: opacity => rgba(232, 230, 240, opacity),
      default: opacity => rgba(255, 255, 255, opacity),
      contrast: opacity => rgba(255, 255, 255, opacity),
      disabled: opacity => rgba(249, 248, 252, opacity),
      hover: opacity => rgba(232, 230, 240, opacity),
      disabledContrast: opacity => rgba(199, 195, 211, opacity),
    },
    quaternary: {
      default: opacity => rgba(249, 248, 252, opacity),
      disabled: opacity => rgba(249, 248, 252, opacity),
      hover: opacity => rgba(232, 230, 240, opacity),
      active: opacity => rgba(232, 230, 240, opacity),
    },
    reversed: {
      active: opacity => rgba(18, 18, 18, opacity),
      default: opacity => rgba(18, 18, 18, opacity),
      disabled: opacity => rgba(232, 230, 240, opacity),
      hover: opacity => rgba(98, 94, 110, opacity),
    },
    secondary: {
      default: opacity => rgba(232, 230, 240, opacity),
      disabled: opacity => rgba(232, 230, 240, opacity),
      hover: opacity => rgba(199, 195, 211, opacity),
      active: opacity => rgba(108, 41, 255, opacity),
    },
    success: {
      default: opacity => rgba(229, 250, 244, opacity),
    },
    tertiary: {
      active: opacity => rgba(199, 195, 211, opacity),
      default: opacity => rgba(199, 195, 211, opacity),
      disabled: opacity => rgba(232, 230, 240, opacity),
      hover: opacity => rgba(98, 94, 110, opacity),
    },
    warning: {
      default: opacity => rgba(250, 246, 230, opacity),
    },
  },
  content: {
    ai: {
      active: opacity => rgba(247, 60, 105, opacity),
      default: opacity => rgba(247, 60, 105, opacity),
      disabled: opacity => rgba(247, 60, 105, opacity),
      hover: opacity => rgba(247, 60, 105, opacity),
    },
    accent: {
      active: opacity => rgba(108, 41, 255, opacity),
      default: opacity => rgba(108, 41, 255, opacity),
      disabled: opacity => rgba(199, 182, 237, opacity),
      hover: opacity => rgba(67, 0, 214, opacity),
    },
    error: {
      default: opacity => rgba(216, 63, 85, opacity),
      contrast: opacity => rgba(165, 34, 53, opacity),
      hoverContrast: opacity => rgba(18, 18, 18, opacity),
    },
    favorited: {
      active: opacity => rgba(250, 138, 165, opacity),
      default: opacity => rgba(250, 138, 165, opacity),
      disabled: opacity => rgba(199, 195, 211, opacity),
      hover: opacity => rgba(247, 60, 105, opacity),
    },
    info: {
      default: opacity => rgba(44, 199, 242, opacity),
      contrast: opacity => rgba(12, 153, 192, opacity),
      hoverContrast: opacity => rgba(18, 18, 18, opacity),
    },
    premium: {
      active: opacity => rgba(239, 196, 75, opacity),
      default: opacity => rgba(239, 196, 75, opacity),
      disabled: opacity => rgba(199, 195, 211, opacity),
      hover: opacity => rgba(235, 163, 19, opacity),
    },
    primary: {
      active: opacity => rgba(18, 18, 18, opacity),
      default: opacity => rgba(18, 18, 18, opacity),
      disabled: opacity => rgba(199, 195, 211, opacity),
      hover: opacity => rgba(18, 18, 18, opacity),
    },
    reversed: {
      active: opacity => rgba(255, 255, 255, opacity),
      default: opacity => rgba(249, 248, 252, opacity),
      disabled: opacity => rgba(232, 230, 240, opacity),
      hover: opacity => rgba(255, 255, 255, opacity),
    },
    secondary: {
      default: opacity => rgba(57, 54, 69, opacity),
      disabled: opacity => rgba(199, 195, 211, opacity),
      hover: opacity => rgba(18, 18, 18, opacity),
      active: opacity => rgba(18, 18, 18, opacity),
    },
    success: {
      default: opacity => rgba(37, 167, 130, opacity),
      contrast: opacity => rgba(22, 100, 78, opacity),
    },
    tertiary: {
      active: opacity => rgba(57, 54, 69, opacity),
      default: opacity => rgba(98, 94, 110, opacity),
      disabled: opacity => rgba(199, 195, 211, opacity),
      hover: opacity => rgba(57, 54, 69, opacity),
    },
    warning: {
      default: opacity => rgba(244, 211, 74, opacity),
      contrast: opacity => rgba(221, 181, 13, opacity),
      hoverContrast: opacity => rgba(18, 18, 18, opacity),
    },
  },
  border: {
    accent: {
      default: opacity => rgba(108, 41, 255, opacity),
    },
    complementary: (color, opacity) => borderComplementary[color](opacity),
    error: {
      default: opacity => rgba(216, 63, 85, opacity),
    },
    info: {
      default: opacity => rgba(44, 199, 242, opacity),
    },
    primary: {
      active: opacity => rgba(108, 41, 255, opacity),
      default: opacity => rgba(199, 195, 211, opacity),
      disabled: opacity => rgba(199, 195, 211, opacity),
      hover: opacity => rgba(57, 54, 69, opacity),
    },
    quaternary: {
      active: opacity => rgba(18, 18, 18, opacity),
      default: opacity => rgba(57, 54, 69, opacity),
      disabled: opacity => rgba(232, 230, 240, opacity),
      hover: opacity => rgba(18, 18, 18, opacity),
    },
    reversed: {
      active: opacity => rgba(255, 255, 255, opacity),
      default: opacity => rgba(255, 255, 255, opacity),
      disabled: opacity => rgba(199, 195, 211, opacity),
      hover: opacity => rgba(249, 248, 252, opacity),
    },
    secondary: {
      active: opacity => rgba(199, 195, 211, opacity),
      default: opacity => rgba(232, 230, 240, opacity),
      disabled: opacity => rgba(249, 248, 252, opacity),
      hover: opacity => rgba(199, 195, 211, opacity),
    },
    success: {
      default: opacity => rgba(37, 167, 130, opacity),
      contrast: opacity => rgba(22, 100, 78, opacity),
    },
    tertiary: {
      active: opacity => rgba(18, 18, 18, opacity),
      default: opacity => rgba(18, 18, 18, opacity),
      disabled: opacity => rgba(199, 195, 211, opacity),
      hover: opacity => rgba(98, 94, 110, opacity),
    },
    warning: {
      default: opacity => rgba(244, 211, 74, opacity),
    },
  },
  shadow: {
    primary: {
      default: opacity => rgba(18, 18, 18, opacity),
    },
    reversed: {
      default: opacity => rgba(255, 255, 255, opacity),
    },
    secondary: {
      default: opacity => rgba(199, 195, 211, opacity),
    },
    accent: {
      default: opacity => rgba(108, 41, 255, opacity),
    },
  },
};

export default color;
